import * as s from "styles/ModalCommonStyled";
import StepperComponent, { StepType } from "components/blocks/stepper/Stepper";
import BigButton from "components/atoms/buttons/BigButton";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";

import TextArea from "components/atoms/input/TextArea";
import PhoneInput from "components/atoms/input/phone/PhoneInput";
import AddressInput from "components/atoms/input/address/AddressInput";
import { UPDATE_ADDRESS_TYPE, UPDATE_TYPE } from "./AddInstallContainer";
import { SearchRecorder } from "components/element/search/SearchRecorder";
import { SelectedRowWithAddress } from "components/element/search/SearchRecorderList";
import {
  InstallReport,
  ReportDataRow,
} from "api/interfaces/reportInterface.interface";
import { UserInfo } from "api/interfaces/userInterface.interface";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import InputDate from "components/atoms/calendar/InputDate";
import {
  ModalAccount,
  SearchAlertAccount,
} from "components/element/search/SearchAlertAccount";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { changeSuspensionStatus } from "utils/AccountUtil";

type Props = {
  steps: Array<StepType>;
  page: number;
  isEdit: boolean | undefined;
  installationReport: InstallReport;
  isFlagSelected: string;
  accountId: string;
  onSelectFlag: (code: string) => void;
  onChangePage: (name: string, index?: number) => void;
  onChangeAddress: (type: UPDATE_ADDRESS_TYPE, address: string) => void;
  onChangeReportInfo: (
    type: UPDATE_TYPE,
    value:
      | string
      | number
      | boolean
      | string[]
      | boolean[]
      | Date
      | SelectedRowWithAddress
  ) => void;
  onClickCreate: () => void;
  onSelectRowChangeAddress?: (row: SelectedRowWithAddress) => void;
  userInfo: UserInfo;
  setIsDeleteConfirm?: (confirm: boolean) => void;
  onConfirmDelete: () => void;
  onDelete: (value: boolean) => void;
  isConfirm: boolean;
  reportRuleValidate: Validate;
  reportRuleValidateMsg: ValidateMsg;
  reportRow?: ReportDataRow;
  saveIsInProgress: boolean;
  deleteIsInProgress: boolean;
  selectedAccount: ProfileAccountInfo;
  targetAccountId: string | undefined;
};

export default function AddInstallPresenter(props: Props): JSX.Element {
  return (
    <>
      <StepperComponent
        activeStep={props.page}
        steps={props.steps}
        onClick={props.onChangePage}
        data-testid="reports-add-installation-stepper-component-change-page"
      />

      <s.ModalInnerHalf>
        <div style={{ display: props.page === 0 ? "block" : "none" }}>
          <s.ModalTableContainer>
            {props.page === 0 && (
              <SearchAlertAccount
                accountId={props.selectedAccount.accountId}
                selectedAccountId={
                  props.targetAccountId ? [props.targetAccountId] : []
                }
                onSelectedRowsChange={(value: string[]) => {
                  props.onChangeReportInfo(UPDATE_TYPE.ACCOUNT, value);
                }}
                isSingleMode={true}
                rowSelectable={(record: ModalAccount) => record.level === "EU"}
                rowExpandable={(record: ModalAccount) =>
                  record.subRows !== undefined && record.subRows.length > 0
                }
                checkDisabled={(record: ModalAccount) =>
                  changeSuspensionStatus(record.status) !==
                  SUSPENSION_STATUS.ENABLED
                }
                disabled={props.isEdit}
              />
            )}
          </s.ModalTableContainer>

          <ValidateWord data-testid="reports-add-installation-validate-recorder">
            {props.reportRuleValidateMsg.isRecorder}
          </ValidateWord>
        </div>
        <div style={{ display: props.page === 1 ? "block" : "none" }}>
          <s.ModalTableContainer>
            {props.page === 1 && (
              <SearchRecorder
                accountId={props.targetAccountId ? props.targetAccountId : ""}
                isSingleMode={true}
                isDisabled={props.isEdit}
                selectedRow={props.installationReport.recorder.recorderId}
                useRowExpandedSelection={true}
                onSelectedRowsChange={(value: string[]) =>
                  props.onChangeReportInfo(UPDATE_TYPE.RECORDER, value)
                }
                onSelectRowChangeAddress={(row: SelectedRowWithAddress) => {
                  props.onChangeReportInfo(UPDATE_TYPE.RECORDER, row);
                }}
                userId={props.isEdit ? props.reportRow?.userId : undefined}
              />
            )}
          </s.ModalTableContainer>
          <ValidateWord data-testid="reports-add-installation-validate-recorder">
            {props.reportRuleValidateMsg.isRecorder}
          </ValidateWord>
        </div>
        <div style={{ display: props.page === 2 ? "block" : "none" }}>
          <s.InstallationGroupTitle>
            <b>Preferences</b>
          </s.InstallationGroupTitle>
          <s.Row>
            <ModalInput
              label="Title"
              essential
              validatedWord={props.reportRuleValidateMsg.isTitle}
              isValidated={!!props.reportRuleValidate.isTitle}
            >
              <Input
                placeholder="Please enter a title"
                defaultValue={props.installationReport.title}
                value={props.installationReport.title}
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(e) =>
                  props.onChangeReportInfo(UPDATE_TYPE.TITLE, e.target.value)
                }
                maxLength={255}
                data-testid="reports-add-installation-input-change-report-info"
              />
            </ModalInput>
          </s.Row>
          <s.InstallationGroupTitle>
            <b>Customer</b>
          </s.InstallationGroupTitle>
          <s.Row>
            <ModalInput
              label="Name"
              essential
              isValidated={props.reportRuleValidate.isCustomName}
              validatedWord={props.reportRuleValidateMsg.isCustomName}
            >
              <Input
                placeholder="Please enter a name"
                defaultValue={props.installationReport.information.customerName}
                value={props.installationReport.information.customerName}
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(e) =>
                  props.onChangeReportInfo(
                    UPDATE_TYPE.CUSTOMER_NAME,
                    e.target.value
                  )
                }
                data-testid="reports-add-installation-input-change-report-info-2"
              />
            </ModalInput>
            <ModalInput
              label="Phone Number"
              essential
              isValidated={props.reportRuleValidate.isCustomPhone}
              validatedWord={props.reportRuleValidateMsg.isCustomPhone}
            >
              <PhoneInput
                selected={
                  props.installationReport.information.customerPhoneNumber
                }
                onSelect={props.onSelectFlag}
                placeholder="Please enter a phone number"
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(phoneNumber: string) =>
                  props.onChangeReportInfo(
                    UPDATE_TYPE.CUSTOMER_PHONE,
                    phoneNumber
                  )
                }
                data-testid="reports-add-installation-phone-input-change-report-info"
              />
            </ModalInput>
          </s.Row>
          <ModalInput
            label="Address"
            essential
            validatedWord={props.reportRuleValidateMsg.isCustomAddress}
            isValidated={!!props.reportRuleValidate.isCustomAddress}
          >
            <AddressInput
              isLoaded={true}
              // defaultValue={
              //   props.installationReport.information.customerLocation
              // }
              value={props.installationReport.information.customerLocation}
              //isDisabled={props.isEdit && !props.installationReport.updatable}
              onChange={(e) =>
                props.onChangeAddress(
                  UPDATE_ADDRESS_TYPE.CUSTOMER,
                  e.currentTarget.value
                )
              }
              onChangeLocationSelect={(
                address: google.maps.LatLngLiteral,
                addressString?: string
              ) => {
                if (addressString !== undefined) {
                  props.onChangeReportInfo(
                    UPDATE_TYPE.CUSTOMER_ADDRESS,
                    addressString
                  );
                }
              }}
              data-testid="reports-add-installation-address-input-change-address"
            />
          </ModalInput>
          <s.InstallationGroupTitle style={{ marginTop: 20 }}>
            <b>Installer</b>
          </s.InstallationGroupTitle>
          <s.Row>
            <ModalInput
              label="Name"
              essential
              isValidated={props.reportRuleValidate.isInstallerName}
              validatedWord={props.reportRuleValidateMsg.isInstallerName}
            >
              <Input
                placeholder="Please enter a name"
                defaultValue={props.userInfo.name}
                value={props.installationReport.information.installerName}
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(e) =>
                  props.onChangeReportInfo(
                    UPDATE_TYPE.INSTALLER_NAME,
                    e.target.value
                  )
                }
                data-testid="reports-add-installation-input-change-report-info-3"
              />
            </ModalInput>
            <ModalInput
              label="Phone Number"
              essential
              isValidated={props.reportRuleValidate.isInstallerPhone}
              validatedWord={props.reportRuleValidateMsg.isInstallerPhone}
            >
              <PhoneInput
                selected={
                  props.installationReport.information.installerPhoneNumber
                }
                onSelect={props.onSelectFlag}
                placeholder="Please enter a phone number"
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(phoneNumber: string) =>
                  props.onChangeReportInfo(
                    UPDATE_TYPE.INSTALLER_PHONE,
                    phoneNumber
                  )
                }
                data-testid="reports-add-installation-phone-input-change-report-info-2"
              />
            </ModalInput>
          </s.Row>
          <s.Row>
            <ModalInput label="Company">
              <Input
                placeholder="Please enter a company name"
                defaultValue={
                  props.installationReport.information.installerCompany
                }
                value={props.installationReport.information.installerCompany}
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(e) =>
                  props.onChangeReportInfo(
                    UPDATE_TYPE.COMPANY_NAME,
                    e.target.value
                  )
                }
                data-testid="reports-add-installation-input-change-report-info-4"
              />
            </ModalInput>
            <ModalInput label="Company Phone Number">
              <PhoneInput
                selected={
                  props.installationReport.information
                    .installerCompanyPhoneNumber
                }
                onSelect={props.onSelectFlag}
                placeholder="Please enter a phone number"
                //disabled={props.isEdit && !props.installationReport.updatable}
                onChange={(phoneNumber: string) =>
                  props.onChangeReportInfo(
                    UPDATE_TYPE.COMPANY_PHONE,
                    phoneNumber
                  )
                }
                data-testid="reports-add-installation-phone-input-change-report-info-3"
              />
            </ModalInput>
          </s.Row>
          <ModalInput label="Company Adddress">
            <AddressInput
              isLoaded={true}
              defaultValue={
                props.installationReport.information.installerCompanyAddress
              }
              value={
                props.installationReport.information.installerCompanyAddress
              }
              //isDisabled={props.isEdit && !props.installationReport.updatable}
              onChange={(e) =>
                props.onChangeAddress(
                  UPDATE_ADDRESS_TYPE.INSTALLER,
                  e.currentTarget.value
                )
              }
              onChangeLocationSelect={(
                address: google.maps.LatLngLiteral,
                addressString?: string
              ) => {
                if (addressString !== undefined) {
                  props.onChangeReportInfo(
                    UPDATE_TYPE.COMPANY_ADDRESS,
                    addressString
                  );
                }
              }}
              data-testid="reports-add-installation-address-input-change-address-2"
            />

            {/* <PhoneInput
               selected={props.isFlagSelected}
               onSelect={props.onSelectFlag}
               placeholder="Enter company address"
               /> */}
          </ModalInput>
          <ModalInput label="Installation Date" half>
            {/* date picker */}
            <InputDate
              placeholder="Enter date"
              value={props.installationReport.information.installationDate}
              //disabled={props.isEdit &&  !props.installationReport.updatable}
              onChange={(value) => {
                props.onChangeReportInfo(UPDATE_TYPE.INSTALL_DATE, value);
              }}
              maxDate={new Date()}
            />
          </ModalInput>
          <ModalInput label="Description">
            <TextArea
              placeholder="Please enter a description"
              value={props.installationReport.information.deviceDescription}
              //disabled={props.isEdit &&  !props.installationReport.updatable}
              onChange={(e) =>
                props.onChangeReportInfo(
                  UPDATE_TYPE.DEVICE_DESC,
                  e.currentTarget.value
                )
              }
              data-testid="reports-add-installation-textarea-change-report-info"
            />
          </ModalInput>
          {/* <s.InstallationGroupTitle>Device</s.InstallationGroupTitle>
             <ModalInput label="Serial">
              <Input placeholder="Enter serial number" />
             </ModalInput>
                       <ModalInput label="Analog Camera Model" half>
              <s.RowMobileNoColumn>
                <Input placeholder="Enter model name" />
                <img src={PLUS_CIRCLE} alt="plus in circle icon" />
              </s.RowMobileNoColumn>
             </ModalInput> */}
        </div>
      </s.ModalInnerHalf>
      <s.ButtonWrapper>
        {props.isEdit && (
          <BigButton
            buttonType="warning"
            label="Delete"
            onClickBtn={() =>
              props.setIsDeleteConfirm !== undefined
                ? props.setIsDeleteConfirm(true)
                : null
            }
            disabled={props.deleteIsInProgress}
            data-testid="reports-add-installation-button-set-is-delete-confirm"
          />
        )}

        {props.page !== 0 && (
          <BigButton
            buttonType="cancel"
            label="Previous"
            onClickBtn={() => props.onChangePage("previous")}
            data-testid="reports-add-installation-button-change-page"
          />
        )}

        {props.page === 2 ? (
          <BigButton
            label="Save"
            onClickBtn={props.onClickCreate}
            //disabled={props.isEdit && !props.installationReport.updatable }
            disabled={props.saveIsInProgress}
            data-testid="reports-add-installation-button-click-create"
          />
        ) : (
          <BigButton
            buttonType="cancel"
            label="Next"
            onClickBtn={() => props.onChangePage("next")}
            data-testid="reports-add-installation-button-change-page-2"
          />
        )}

        {props.isConfirm && (
          <DeleteConfirmModal
            label="report rule"
            onDelete={() => props.onDelete(false)}
            onConfirmDelete={props.onConfirmDelete}
          />
        )}
      </s.ButtonWrapper>
    </>
  );
}
